import { DomainCode, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { deleteSearch, getSearchesByAssignedToSub } from '@/apis/searches';
import {
  deleteSearchTemplate,
  getSearchTemplateById,
  getSearchTemplates,
} from '@/apis/searchtemplates';

export const useSearches = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  return useQuery({
    queryKey: ['searches', user?.sub],
    queryFn: async () =>
      getSearchesByAssignedToSub(await getAccessTokenSilently(), user?.sub ?? ''),
  });
};

export const useSearchTemplates = (domainCode: DomainCode) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['searchtemplates', domainCode],
    queryFn: async () => getSearchTemplates(await getAccessTokenSilently(), domainCode),
  });
};

export const useDeleteSearch = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { enqueueSuccessNotification } = useSaladBar();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ searchId }: { searchId: number }) =>
      deleteSearch(await getAccessTokenSilently(), searchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['searches'] });
      enqueueSuccessNotification('Search deleted');
    },
  });
};

export const useSearchTemplateByIdQuery = (templateId: number, enabled: boolean) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['searchtemplateById', templateId],
    queryFn: async () => getSearchTemplateById(await getAccessTokenSilently(), templateId),
    enabled: enabled && templateId > 0,
    retry: false,
  });
};

export const useDeleteSearchTemplate = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { enqueueSuccessNotification } = useSaladBar();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ searchTemplateId }: { searchTemplateId: number }) =>
      deleteSearchTemplate(await getAccessTokenSilently(), searchTemplateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['searchtemplates'] });
      enqueueSuccessNotification('Search template deleted');
    },
  });
};
